import { ReactElement, useMemo, useState } from 'react';
import { Autocomplete, TAutocompleteOption, Typography } from '@weareopenr/component-library';
import { IMenuLabel } from 'src/api/getMenuLabels';
import { getMenuLabelNameError, getNewMenuLabelName, toNewMenuLabelValue } from 'src/utils/menuLabels/menuLabels';
import styles from './MenuLabelsField.module.scss';

const CREATE_HINT_VALUE = 'create-hint';

interface IMenuLabelsFieldProps {
  /** The brand's saved menu labels. */
  menuLabels?: IMenuLabel[];
  /** Label ids, plus new labels as values from `toNewMenuLabelValue`. */
  value: string[];
  onChange: (value: string[]) => void;
  loading?: boolean;
}

/**
 * Picks menu labels, or creates one from the typed name with Enter. A new label is only held in `value` until the
 * form is saved, so cancelling discards it.
 */
export const MenuLabelsField = ({ menuLabels, value, onChange, loading }: IMenuLabelsFieldProps): ReactElement => {
  const [searchTerm, setSearchTerm] = useState('');

  const labelOptions = useMemo((): TAutocompleteOption[] => {
    const newLabels = value.flatMap((labelValue) => {
      const name = getNewMenuLabelName(labelValue);
      return name === null ? [] : [{ value: labelValue, label: name }];
    });
    const savedLabels = (menuLabels ?? []).map(({ menu_label_id, name }) => ({ value: menu_label_id, label: name }));
    return [...savedLabels, ...newLabels];
  }, [menuLabels, value]);

  const nameError = getMenuLabelNameError(
    searchTerm,
    labelOptions.map(({ label }) => label),
  );

  const options = useMemo((): TAutocompleteOption[] => {
    if (!searchTerm) {
      const hint: TAutocompleteOption = {
        value: CREATE_HINT_VALUE,
        label: 'or type to create a new label',
        content: (
          <Typography size="medium" className={styles['create-row']}>
            or type to <strong>create a new label</strong>
          </Typography>
        ),
        disabled: true,
      };
      return [...labelOptions, hint];
    }

    // A blank name lists nothing, so Enter can't pick another label.
    const name = searchTerm.trim();
    if (!name) return [];

    const term = name.toLocaleLowerCase();
    const matches = labelOptions.filter(({ label }) => label.toLocaleLowerCase().includes(term));
    if (nameError) return matches;

    // Listed first so Enter picks it.
    const createOption: TAutocompleteOption = {
      value: toNewMenuLabelValue(name),
      label: name,
      content: (
        <Typography size="medium" className={styles['create-row']}>
          Create <strong>&quot;{name}&quot;</strong>
        </Typography>
      ),
    };
    return [createOption, ...matches];
  }, [labelOptions, searchTerm, nameError]);

  return (
    <Autocomplete
      multiple
      name="menuLabels"
      label="Add Menu Label(s)"
      options={options}
      value={value}
      onChange={onChange}
      filter="none"
      onSearchChange={setSearchTerm}
      loading={loading}
      error={nameError}
      limitChips={3}
    />
  );
};

export default MenuLabelsField;
